// One OTA on the changelog timeline: its date on the rail, its card, and an
// expandable panel per app (what Scouts were shown, what changed).

import { useState } from 'react';
import { DevicePhoneMobileIcon } from '@heroicons/react/24/outline';
import { cx } from '../../../../components/admin/console';
import { AppIcon, appIconRadius } from './AppIcon';
import { AuthorMark, Chevron, CommitChip, CopyButton, CopyLinkButton, ExternalLink, KIND_STYLE, KindPills, Notice, SectionLabel } from './parts';
import { KbChanges, type KbGroup } from './KbChanges';
import {
  ago,
  appKindCounts,
  kbChangesOf,
  appMarkdown,
  appNames,
  appText,
  nzShortDay,
  nzTime,
  otaMarkdown,
  otaText,
  releaseKindCounts,
  repeatsOldNotes,
  updateKindCounts,
  type ChangeSection,
  type ChangelogAppUpdate,
  type ChangelogOta,
  type ChangelogRelease,
  type NoteLine,
} from '../data/changelog';

/** Lines shown per section before "Show N more". */
export const SECTION_PREVIEW = 6;

export type CopyFormat = 'text' | 'markdown';

export interface UpdateCardProps {
  ota: ChangelogOta;
  id: string;
  /** The apps to show: all of them, or only the filtered one. */
  apps: ChangelogAppUpdate[];
  latest: boolean;
  open: boolean;
  isAppOpen: (app: string) => boolean;
  onToggle: () => void;
  onToggleApp: (app: string) => void;
  linkTo: () => string;
  format: CopyFormat;
  now: number;
}

export function UpdateCard({ ota, id, apps, latest, open, isAppOpen, onToggle, onToggleApp, linkTo, format, now }: UpdateCardProps) {
  const counts = updateKindCounts(apps);
  const shown: ChangelogOta = { ...ota, apps };
  // The month heading names the month, so the rail needs only the day.
  const [dayWord, dayNum] = nzShortDay(ota.firstOfferedAt).split(' ');
  const headId = `update-${id}-title`;
  return (
    <div id={`update-${id}`} className="scroll-mt-20 sm:grid sm:grid-cols-[76px_minmax(0,1fr)] sm:gap-4">
      <div className="relative hidden sm:block" aria-hidden="true">
        <div className="absolute bottom-[-12px] right-[4px] top-0 w-0.5 bg-slate-800" />
        <div className={cx('absolute right-0 top-[18px] h-2.5 w-2.5 rounded-full ring-[3px] ring-slate-900', latest ? 'bg-cyan-400' : 'bg-slate-600')} />
        <div className="pr-[22px] pt-3 text-right">
          <div className="text-[26px] font-bold leading-none text-white">{dayNum}</div>
          <div className="mt-1 text-[13px] text-slate-400">{dayWord}</div>
        </div>
      </div>

      <article
        aria-labelledby={headId}
        className={cx(
          'min-w-0 rounded-2xl border bg-slate-800/50',
          latest ? 'border-cyan-400/35 shadow-[0_8px_30px_-12px_rgba(34,211,238,0.28)]' : 'border-slate-700',
        )}
      >
        {/* The whole header opens the update: the title button's ::after covers it, and only the copy controls sit above. */}
        <div className="relative rounded-2xl py-3 pl-2 pr-2 transition-colors hover:bg-slate-700/20 sm:py-3.5 sm:pl-3 sm:pr-3">
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onToggle}
              aria-expanded={open}
              aria-controls={`update-${id}-body`}
              className="flex min-w-0 flex-1 items-start gap-2.5 text-left after:absolute after:inset-0 after:rounded-2xl focus:outline-none focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-cyan-500 sm:gap-3"
            >
              <Chevron open={open} className="mt-0.5 h-5 w-5" />
              <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
                  <span id={headId} className="text-base font-semibold text-white">
                    {/* On wider screens the date sits on the rail, which screen readers skip, so it stays here for them. */}
                    <span className="sm:sr-only">{nzShortDay(ota.firstOfferedAt)}</span>
                    <span className="hidden sm:inline">{appNames(apps)}</span>
                  </span>
                  {latest && <span className="rounded-full border border-cyan-400/30 bg-cyan-400/10 px-2.5 py-0.5 text-[13px] font-semibold leading-5 text-cyan-300">Latest</span>}
                </span>
                <span className="text-sm text-slate-400">Offered {nzTime(ota.firstOfferedAt)} · {ago(ota.firstOfferedAt, now)}</span>
              </span>
            </button>
            <span className="relative z-10 flex flex-none items-center gap-1 sm:gap-1.5">
              <KindPills counts={counts} className="mr-1.5 hidden flex-nowrap lg:flex" />
              <CopyLinkButton getUrl={linkTo} ariaLabel={`Copy a link to the ${nzShortDay(ota.firstOfferedAt)} update`} />
              <CopyButton getText={() => (format === 'markdown' ? otaMarkdown(shown) : otaText(shown))} ariaLabel={`Copy the ${nzShortDay(ota.firstOfferedAt)} update's notes`} />
            </span>
          </div>
          {/* Lined up under the title, using the card's full width. */}
          <div className="mt-2.5 flex flex-col gap-2.5 pl-[30px] sm:pl-8">
            <ul className="flex flex-wrap gap-1.5" aria-label="Apps in this update">
              {apps.map((a) => (
                <li key={a.app}>
                  <AppChip name={a.name} app={a.app} version={a.toVersion} live={a.live} />
                </li>
              ))}
            </ul>
            <KindPills counts={counts} className="lg:hidden" />
          </div>
        </div>

        {open && (
          <div id={`update-${id}-body`} className="space-y-2.5 border-t border-slate-700/60 p-2 sm:p-3">
            <KbChanges groups={kbGroups(apps)} id={`update-${id}-kb`} heading={`Knowledge base changes, ${nzShortDay(ota.firstOfferedAt)}`} format={format} />
            {apps.map((a) => (
              <AppPanel key={a.app} update={a} open={isAppOpen(a.app)} onToggle={() => onToggleApp(a.app)} format={format} panelId={`update-${id}-${a.app}`} />
            ))}
          </div>
        )}
      </article>
    </div>
  );
}

/** Knowledge base changes, app by app. */
export function kbGroups(apps: ChangelogAppUpdate[]): KbGroup[] {
  return apps.map((a) => ({ id: a.app, name: a.name, icon: <AppIcon app={a.app} size={24} />, items: kbChangesOf(a) }));
}

/** Chip padding: with the icon's own corners it makes the chip's corners follow the icon's. */
const CHIP_PAD = 3;
const CHIP_ICON = 26;

function AppChip({ app, name, version, live }: { app: string; name: string; version: string; live: boolean }) {
  return (
    <span
      style={{ padding: CHIP_PAD, paddingRight: 12, borderRadius: appIconRadius(CHIP_ICON) + CHIP_PAD + 1 }}
      className="inline-flex items-center gap-2 whitespace-nowrap border border-slate-700 bg-slate-950/50 text-sm text-slate-200"
    >
      <AppIcon app={app} size={CHIP_ICON} />
      {name}
      <span className="font-mono text-[13px] text-slate-300">{version}</span>
      {live && <span role="img" aria-label="On Scouts now" title="On Scouts now" className="h-2 w-2 rounded-full bg-emerald-400" />}
    </span>
  );
}

/** What a release's status pill says. Pending releases carry a stage; sent ones don't. */
function releaseStatus(r: ChangelogRelease, prNumber: number | null): { label: string; className: string } {
  if (r.stage === 'proposed') return { label: prNumber ? `In release PR #${prNumber}` : 'In a release PR', className: 'border-amber-400/30 bg-amber-400/10 text-amber-300' };
  if (r.stage === 'released') return { label: 'Released, not sent yet', className: 'border-cyan-400/30 bg-cyan-400/10 text-cyan-300' };
  return r.sentToScouts
    ? { label: 'Sent to Scouts', className: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300' }
    : { label: 'Not sent on its own', className: 'border-slate-500/30 bg-slate-500/10 text-slate-300' };
}

export interface AppPanelProps {
  update: ChangelogAppUpdate;
  open: boolean;
  onToggle: () => void;
  format: CopyFormat;
  panelId: string;
  /** Set for an app that is not on Scouts yet: its open release PR, if any. */
  pending?: { pr: { number: number; url: string } | null };
}

export function AppPanel({ update: a, open, onToggle, format, panelId, pending }: AppPanelProps) {
  const counts = appKindCounts(a);
  const several = a.releases.length > 1;
  const skipped = a.releases.filter((r) => !r.sentToScouts).length;
  const prNumber = pending?.pr?.number ?? null;
  return (
    <section className="rounded-xl border border-slate-700/65 bg-slate-950/35" aria-label={`${a.name} ${a.toVersion}`}>
      <div className="flex items-center gap-1 rounded-xl px-1.5 py-1.5 transition-colors hover:bg-slate-700/25 sm:gap-2 sm:px-2.5">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={`${panelId}-body`}
          className="flex min-h-[48px] min-w-0 flex-1 items-center gap-3 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          <Chevron open={open} className="h-[18px] w-[18px]" />
          <AppIcon app={a.app} size={36} />
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="text-base font-semibold text-white">{a.name}</span>
            <span className="rounded-md border border-slate-700 bg-slate-900 px-2 py-0.5 font-mono text-[13px] text-slate-400">
              {a.fromVersion && <>{a.fromVersion} <span className="text-slate-500">→</span> </>}
              <span className="text-white">{a.toVersion}</span>
            </span>
            {pending && a.releases[0] && (() => {
              const st = releaseStatus(a.releases[0], prNumber);
              return <span className={cx('rounded-full border px-2.5 py-0.5 text-[13px] font-semibold', st.className)}>{st.label}</span>;
            })()}
            <KindPills counts={counts} icons={false} className="hidden md:flex" />
            {counts.length > 0 && <span className="basis-full text-sm text-slate-400 md:hidden">{counts.map((k) => k.label).join(' · ')}</span>}
          </span>
        </button>
        <CopyButton iconOnly getText={() => (format === 'markdown' ? appMarkdown(a) : appText(a))} ariaLabel={`Copy ${a.name} notes`} />
      </div>

      {open && (
        <div id={`${panelId}-body`} className="space-y-6 px-3 pb-5 pt-1.5 sm:pl-[60px] sm:pr-5">
          {several && pending && (
            <Notice tone="info">
              Scouts are on {a.fromVersion}. {a.releases.length} releases are on their way, newest first.
            </Notice>
          )}
          {several && !pending && (
            <Notice tone="info">
              This update covers {a.releases.length} releases. Scouts went from {a.fromVersion} straight to {a.toVersion}
              {skipped > 0 && <>; the {skipped === 1 ? 'one' : skipped} in between {skipped === 1 ? 'was' : 'were'} never sent on {skipped === 1 ? 'its' : 'their'} own, so {skipped === 1 ? 'its' : 'their'} changes are here too</>}.
              {' '}Only the release Scouts got starts open.
            </Notice>
          )}

          {repeatsOldNotes(a) ? (
            <div>
              <SectionLabel>Will be shown on Scouts</SectionLabel>
              <OldNotes update={a} panelId={panelId} />
            </div>
          ) : (
            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <SectionLabel>{pending ? 'Will be shown on Scouts' : 'Shown on Scouts'}</SectionLabel>
                {a.notesSameAs && (
                  <span
                    title="RELEASE_NOTES.md wasn't changed for this version, so Scouts were shown the same text again."
                    className="inline-flex rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 text-[13px] font-semibold text-amber-400"
                  >
                    Same notes as {a.notesSameAs}
                  </span>
                )}
              </div>
              <div className="mt-2.5 overflow-hidden rounded-xl border border-slate-700 bg-slate-900">
                <div className="flex items-center gap-2.5 border-b border-slate-700/60 bg-slate-800/45 px-4 py-2.5 text-[13px] text-slate-400">
                  <DevicePhoneMobileIcon className="h-4 w-4 flex-none" aria-hidden="true" />
                  Software Updates · {a.name} {a.toVersion}
                </div>
                <div className="px-4 pb-4 pt-3.5 text-[15px] leading-relaxed text-slate-300 sm:px-[18px]">
                  {a.customerNotes?.length ? <CustomerNotes lines={a.customerNotes} /> : <span className="text-slate-400">{pending ? 'No RELEASE_NOTES.md on main yet.' : `No notes found for ${a.toVersion}.`}</span>}
                </div>
              </div>
            </div>
          )}

          <div>
            <SectionLabel>What changed</SectionLabel>
            {a.missingRelease && (
              <div className="mt-2.5">
                <Notice tone="warning">Scouts were offered {a.toVersion}, but GitHub has no release for it, so there are no changes to show.</Notice>
              </div>
            )}
            {a.releases.map((r) => (
              <ReleaseBlock key={r.tag} release={r} folds={several} prNumber={prNumber} />
            ))}
            <div className="mt-3 flex flex-wrap gap-x-6">
              {a.releases[0] && (
                <ExternalLink href={a.releases[0].url}>
                  {a.releases[0].stage === 'proposed' ? `Release PR #${prNumber ?? ''} on GitHub` : `Release ${a.releases[0].version} on GitHub`}
                </ExternalLink>
              )}
              {a.compareUrl && <ExternalLink href={a.compareUrl}>Every code change {a.fromVersion} → {a.toVersion}</ExternalLink>}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/**
 * On its way, but RELEASE_NOTES.md still holds the live version's message.
 * That is normal until the message is written just before release, so it is
 * one quiet line, with the old message folded away rather than read as new.
 */
function OldNotes({ update: a, panelId }: { update: ChangelogAppUpdate; panelId: string }) {
  const [show, setShow] = useState(false);
  return (
    <div className="mt-2">
      <p className="text-[15px] leading-relaxed text-slate-400">
        <span className="font-medium text-slate-300">No message yet.</span> It's written just before release. Until then Scouts would show {a.notesSameAs}'s message.
      </p>
      {a.customerNotes?.length ? (
        <>
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            aria-expanded={show}
            aria-controls={`${panelId}-old-notes`}
            className="-ml-1 mt-1 inline-flex min-h-[36px] items-center gap-1.5 rounded-md px-1 text-sm font-medium text-slate-400 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
          >
            <Chevron open={show} className="h-4 w-4" />
            Show {a.notesSameAs}'s message
          </button>
          {show && (
            <div id={`${panelId}-old-notes`} className="mt-1.5 rounded-xl border border-slate-700/60 px-4 py-3.5 text-[15px] leading-relaxed text-slate-500 sm:px-[18px] [&_p]:text-slate-500 [&_strong]:text-slate-400">
              <CustomerNotes lines={a.customerNotes} />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function ReleaseBlock({ release: r, folds, prNumber }: { release: ChangelogRelease; folds: boolean; prNumber: number | null }) {
  // Sent updates open the release Scouts got; on-its-way lists open every release.
  const [open, setOpen] = useState(r.sentToScouts || !!r.stage);
  const status = releaseStatus(r, prNumber);
  const body = (
    <>
      {r.note && <p className="mt-2.5 whitespace-pre-line text-[15px] leading-relaxed text-slate-300">{r.note}</p>}
      {r.sections.map((s) => <Section key={s.kind + s.title} section={s} />)}
    </>
  );
  if (!folds) return body;
  const summary = releaseKindCounts(r).map((k) => k.label).join(' · ');
  return (
    <div className="mt-3 rounded-xl border border-slate-700/60 bg-slate-900/50 px-3 pb-3 pt-1 sm:px-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex min-h-[44px] w-full flex-wrap items-center gap-x-2.5 gap-y-1 rounded-lg text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
      >
        <span className="font-mono text-[15px] font-semibold text-white">{r.version}</span>
        {r.publishedAt && <span className="text-sm text-slate-400">released {nzShortDay(r.publishedAt).replace(/^\w+ /, '')}</span>}
        <span className={cx('rounded-full border px-2.5 py-0.5 text-[13px] font-semibold', status.className)}>{status.label}</span>
        {summary && <span className="text-sm text-slate-400">{summary}</span>}
        <span className="ml-auto">
          <Chevron open={open} className="h-[18px] w-[18px]" />
        </span>
      </button>
      {open && (
        <div className="pb-1">
          {body}
          <div className="mt-2">
            <ExternalLink href={r.url}>{r.stage === 'proposed' ? `Release PR #${prNumber ?? ''} on GitHub` : `Release ${r.version} on GitHub`}</ExternalLink>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ section: s }: { section: ChangeSection }) {
  const [all, setAll] = useState(false);
  const style = KIND_STYLE[s.kind];
  const shown = all ? s.items : s.items.slice(0, SECTION_PREVIEW);
  const hidden = s.items.length - shown.length;
  return (
    <div className="mt-3.5">
      <div className={cx('flex items-center gap-2 text-sm font-semibold', style.head)}>
        <style.Icon className="h-4 w-4" aria-hidden="true" />
        {s.title} <span className="font-medium text-slate-500">{s.items.length}</span>
      </div>
      <ul className="mt-1.5">
        {shown.map((it, i) => (
          <li key={`${it.sha ?? ''}-${i}`} className="flex items-baseline gap-3 py-[5px] text-[15px] leading-snug text-slate-300">
            <span className={cx('h-1.5 w-1.5 flex-none -translate-y-0.5 rounded-full', style.dot)} aria-hidden="true" />
            <span className="min-w-0 flex-1">{it.text}</span>
            {it.sha && it.url && (
              <span className="flex flex-none items-center gap-2">
                {it.author && <AuthorMark author={it.author} />}
                <CommitChip sha={it.sha} url={it.url} />
              </span>
            )}
          </li>
        ))}
      </ul>
      {(hidden > 0 || all) && s.items.length > SECTION_PREVIEW && (
        <button
          type="button"
          onClick={() => setAll((v) => !v)}
          className="mt-1.5 inline-flex min-h-[36px] items-center rounded-lg border border-slate-700 px-3 text-sm font-medium text-slate-300 hover:bg-slate-800 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500"
        >
          {all ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

function CustomerNotes({ lines }: { lines: NoteLine[] }) {
  const bullets = lines.filter((l) => l.kind === 'bullet');
  const texts = lines.filter((l) => l.kind === 'text');
  return (
    <>
      {bullets.length > 0 && (
        <ul className="list-disc space-y-2 pl-[18px] marker:text-slate-500">
          {bullets.map((l, i) => (
            <li key={i}>
              {l.lead && <strong className="font-semibold text-white">{l.lead}</strong>} {l.text}
            </li>
          ))}
        </ul>
      )}
      {texts.map((l, i) => (
        <p key={i} className={cx('text-slate-400', (bullets.length > 0 || i > 0) && 'mt-3')}>
          {l.text}
        </p>
      ))}
    </>
  );
}
